import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "../lib/cx";

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
	/** Accessible name AND the tooltip. Required — an icon alone is not a label. */
	label: string;
	icon: ReactNode;
	size?: "xs" | "sm" | "md";
	/**
	 * `row` / `rowDanger` — the designer's row action (prototype `.ab`): an
	 * 8px-radius square in ink-2 that lights navy-soft (or bad-soft) on hover.
	 * 2rem rather than her 36px, so a row stays compact.
	 * They ignore `size`. Need the app theme's `gms-*` colours.
	 */
	variant?: "ghost" | "outline" | "primary" | "danger" | "row" | "rowDanger";
}

const SIZE = { xs: "btn-xs", sm: "btn-sm", md: "" } as const;
const VARIANT = {
	ghost: "btn-ghost text-base-content/70 hover:text-base-content",
	outline: "btn-ghost border-base-300 bg-base-100 hover:bg-base-200",
	primary: "btn-primary",
	danger: "btn-ghost text-error hover:bg-error/10",
} as const;

const ROW_BASE =
	"grid size-[2rem] cursor-pointer place-items-center rounded-[0.5rem] text-gms-ink-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:cursor-not-allowed disabled:opacity-30 [&>svg]:size-[1.125rem]";
const ROW = {
	row: "hover:enabled:bg-gms-navy-soft hover:enabled:text-gms-navy",
	rowDanger: "hover:enabled:bg-gms-bad-soft hover:enabled:text-gms-bad",
} as const;

/**
 * Square icon-only button — the platform's row action (the eye for "view",
 * pencil for "edit"…). Same shape everywhere so a desk worker's hand learns
 * where it is.
 */
export function IconButton({ label, icon, size = "sm", variant = "ghost", className, ...rest }: IconButtonProps) {
	if (variant === "row" || variant === "rowDanger")
		return (
			<button type="button" aria-label={label} title={label} className={cx(ROW_BASE, ROW[variant], className)} {...rest}>
				{icon}
			</button>
		);
	return (
		<button
			type="button"
			aria-label={label}
			title={label}
			className={cx("btn btn-square", SIZE[size], VARIANT[variant], "[&>svg]:size-[1.125rem]", className)}
			{...rest}
		>
			{icon}
		</button>
	);
}
